import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { AssetPicker } from '../assets/AssetPicker';
import { gql } from '../../graphql/client';
import {
  M_UPDATE_FRONTEND_APP_SETTINGS,
  Q_GET_FRONTEND_APP,
} from '../../graphql/operations';
import type { FrontendApp } from '../../types/forge';
import { AndroidSigningSection } from './AndroidSigningSection';
import { BuildSection } from './BuildSection';
import { ContentSection } from './ContentSection';
import { NavigationStructureSection } from './NavigationStructureSection';
import { ThemeSection, type ThemeShape } from './ThemeSection';
import { GenerateAppModal } from '../ai/GenerateAppModal';

/**
 * App Settings — the single home for everything that's app-level
 * (vs. view-level, which the View Builder owns).
 *
 * Layout: a left-rail TOC + a scrollable column of grouped sections.
 * Sections are anchored (`<section id="…">`) and the TOC links scroll
 * to them. Logical grouping keeps the page navigable as it grows:
 *
 *   Identity   — name, bundle / package, deep link
 *   Branding   — assets (icon, splash) + theme (colors, font)
 *   Navigation — top-level navigation structure (templates, replace)
 *   Content    — externalised app-level strings (i18n / overrides)
 *   Build      — Android signing + native build trigger + history
 *
 * Theme + Content used to live in the View Builder right rail, but
 * they're app-level concerns and were diluting the View Builder's
 * focus on view-tree authoring. They've moved here.
 */
export function AppSettingsScreen() {
  const { appId } = useParams<{ appId: string }>();
  const queryClient = useQueryClient();

  const app = useQuery({
    queryKey: ['frontend-app', appId],
    enabled: !!appId,
    queryFn: () =>
      gql<{ FrontendApp_by_pk: (FrontendApp & { theme?: ThemeShape | null }) | null }>(
        Q_GET_FRONTEND_APP,
        { id: appId },
      ),
  });

  const fa = app.data?.FrontendApp_by_pk ?? null;

  const [form, setForm] = useState<{
    name: string;
    bundle_identifier: string;
    android_package: string;
    deep_link_scheme: string;
    icon_asset_url: string;
    splash_asset_url: string;
  }>({
    name: '',
    bundle_identifier: '',
    android_package: '',
    deep_link_scheme: '',
    icon_asset_url: '',
    splash_asset_url: '',
  });
  const [dirty, setDirty] = useState(false);
  const [pickerKind, setPickerKind] = useState<'icon' | 'splash' | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (!fa) return;
    setForm({
      name: fa.name ?? '',
      bundle_identifier: fa.bundle_identifier ?? '',
      android_package: fa.android_package ?? '',
      deep_link_scheme: fa.deep_link_scheme ?? '',
      icon_asset_url: fa.icon_asset_url ?? '',
      splash_asset_url: fa.splash_asset_url ?? '',
    });
    setDirty(false);
  }, [fa?.id, fa?.name, fa?.bundle_identifier, fa?.android_package,
      fa?.deep_link_scheme, fa?.icon_asset_url, fa?.splash_asset_url]);

  const save = useMutation({
    mutationFn: () =>
      gql(M_UPDATE_FRONTEND_APP_SETTINGS, {
        id: appId,
        name: form.name,
        bundle_identifier: form.bundle_identifier || null,
        android_package: form.android_package || null,
        deep_link_scheme: form.deep_link_scheme || null,
        icon_asset_url: form.icon_asset_url || null,
        splash_asset_url: form.splash_asset_url || null,
      }),
    onSuccess() {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['frontend-app', appId] });
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  if (!appId) return <div style={s.empty}>Missing appId</div>;
  if (app.isLoading) return <div style={s.empty}>Loading…</div>;
  if (app.error) return <div style={s.empty}>Failed to load: {(app.error as Error).message}</div>;
  if (!fa) return <div style={s.empty}>App not found.</div>;

  return (
    <div style={s.shell}>
      <header style={s.header}>
        <Link to="/" style={s.back}>← Apps</Link>
        <h1 style={s.title}>{fa.name}</h1>
        {dirty && (
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            style={s.saveStickyBtn}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        )}
        <Link to="/assets" style={s.assetsLink}>Assets library</Link>
        <Link to={`/apps/${appId}/builder`} style={s.openBuilder}>
          Open View Builder →
        </Link>
      </header>

      <div style={s.body}>
        <nav style={s.toc} aria-label="App settings sections">
          <ul style={s.tocList}>
            {SECTIONS.map((sec) => (
              <li key={sec.id}>
                <a href={`#${sec.id}`} style={s.tocLink}>
                  <span style={s.tocDot} />
                  {sec.label}
                </a>
              </li>
            ))}
          </ul>
          {save.isError && (
            <p style={s.tocError}>{(save.error as Error).message}</p>
          )}
        </nav>

        <main style={s.main}>
          {/* ─── Identity ──────────────────────────────────────────── */}
          <SectionCard
            id="identity"
            title="Identity"
            hint="Visible to users. Title appears as the app's display name; bundle / package identify the binary."
          >
            <Field label="Title">
              <input value={form.name} onChange={(e) => update('name', e.target.value)} style={s.input} />
            </Field>
            <Field label="iOS Bundle Identifier" hint="e.g. com.acme.app">
              <input
                value={form.bundle_identifier}
                onChange={(e) => update('bundle_identifier', e.target.value)}
                placeholder="com.example.app"
                style={s.input}
              />
            </Field>
            <Field label="Android Package" hint="e.g. com.acme.app">
              <input
                value={form.android_package}
                onChange={(e) => update('android_package', e.target.value)}
                placeholder="com.example.app"
                style={s.input}
              />
            </Field>
            <Field label="Deep Link Scheme" hint="The scheme part of acme://… links into your app.">
              <input
                value={form.deep_link_scheme}
                onChange={(e) => update('deep_link_scheme', e.target.value)}
                placeholder="acmeapp"
                style={s.input}
              />
            </Field>
          </SectionCard>

          {/* ─── Branding (Assets + Theme) ─────────────────────────── */}
          <SectionGroup id="branding" label="Branding">
            <SectionCard
              title="Brand assets"
              hint={
                <>
                  Pick from your{' '}
                  <Link to="/assets" style={s.inlineLink}>asset library</Link>{' '}
                  or upload a new file directly. URLs are stored once selected so
                  the runtime can resolve them.
                </>
              }
            >
              <Field label="App Icon" hint="Square PNG, ideally 1024×1024. Used for the home-screen icon.">
                <AssetField
                  value={form.icon_asset_url}
                  onChange={(v) => update('icon_asset_url', v)}
                  previewSize={64}
                  onPick={() => setPickerKind('icon')}
                />
              </Field>
              <Field label="Splash Screen" hint="Shown on app launch before the JS bundle is ready.">
                <AssetField
                  value={form.splash_asset_url}
                  onChange={(v) => update('splash_asset_url', v)}
                  previewSize={120}
                  onPick={() => setPickerKind('splash')}
                />
              </Field>
            </SectionCard>

            <SectionCard
              title="Theme"
              hint="Colors and typography applied across the app. Engine-side, the theme drives the background tint and is available to view styles via tokens."
            >
              <ThemeSection appId={appId} theme={fa.theme ?? null} />
            </SectionCard>
          </SectionGroup>

          {/* ─── Navigation ────────────────────────────────────────── */}
          <SectionGroup id="navigation" label="Navigation">
            <NavigationStructureSection appId={appId} mainViewId={fa.main_view_id ?? null} />
            <SectionCard
              title="Generate with AI"
              hint="Describe the app in plain English. Forge's AI writes the screens, navigation, theme, and interactions; you preview the proposed tree before committing."
            >
              <button
                type="button"
                style={s.aiBtn}
                onClick={() => setAiOpen(true)}
              >
                Generate App with AI…
              </button>
            </SectionCard>
          </SectionGroup>

          {/* ─── Content ───────────────────────────────────────────── */}
          <SectionGroup id="content" label="Content">
            <SectionCard
              title="External content"
              hint="App-level externalized strings (copy, labels, microcopy) authors override per-app without touching the view tree. Reference by `${Content.alias}` from any text."
            >
              <ContentSection appId={appId} />
            </SectionCard>
          </SectionGroup>

          {/* ─── Build & Release ───────────────────────────────────── */}
          <SectionGroup id="build" label="Build & Release">
            <AndroidSigningSection />
            <BuildSection appId={appId} app={fa} />
          </SectionGroup>
        </main>
      </div>

      <GenerateAppModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        appId={appId}
        hasExistingRoot={!!fa.main_view_id}
      />

      <AssetPicker
        open={pickerKind !== null}
        onClose={() => setPickerKind(null)}
        kind={pickerKind ?? 'icon'}
        title={pickerKind === 'icon' ? 'Select App Icon' : 'Select Splash Screen'}
        onPick={(url) => {
          if (pickerKind === 'icon') update('icon_asset_url', url);
          else if (pickerKind === 'splash') update('splash_asset_url', url);
        }}
      />
    </div>
  );
}

/* ============================== Subviews ================================ */

const SECTIONS: Array<{ id: string; label: string }> = [
  { id: 'identity', label: 'Identity' },
  { id: 'branding', label: 'Branding' },
  { id: 'navigation', label: 'Navigation' },
  { id: 'content', label: 'Content' },
  { id: 'build', label: 'Build & Release' },
];

function SectionGroup({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} style={s.group} aria-label={label}>
      <h2 style={s.groupHeading}>{label}</h2>
      {children}
    </section>
  );
}

function SectionCard({
  id,
  title,
  hint,
  children,
}: {
  id?: string;
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} style={s.card}>
      <h3 style={s.cardTitle}>{title}</h3>
      {hint && <p style={s.cardHint}>{hint}</p>}
      {children}
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={s.fieldHint}>{hint}</span>}
    </label>
  );
}

function AssetField({
  value,
  onChange,
  onPick,
  previewSize,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: () => void;
  previewSize: number;
}) {
  return (
    <div style={s.assetRow}>
      {value ? (
        <img
          src={value}
          alt="preview"
          width={previewSize}
          height={previewSize}
          style={{ ...s.preview, width: previewSize, height: previewSize }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.opacity = '0.2';
          }}
        />
      ) : (
        <div style={{ ...s.previewEmpty, width: previewSize, height: previewSize }}>
          no preview
        </div>
      )}
      <div style={s.assetControls}>
        <button type="button" onClick={onPick} style={s.pickBtn}>
          {value ? 'Replace…' : 'Pick from library…'}
        </button>
        {value && (
          <button type="button" onClick={() => onChange('')} style={s.clearBtn}>
            Clear
          </button>
        )}
        {value && <span style={s.assetUrl} title={value}>{value}</span>}
      </div>
    </div>
  );
}

/* ================================ Styles ================================ */

const s: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    backgroundColor: '#F8FAFC',
    fontFamily: 'system-ui, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 24px',
    borderBottom: '1px solid #E2E8F0',
    backgroundColor: '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 5,
  },
  back: { fontSize: 13, color: '#4F46E5', textDecoration: 'none' },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A', flex: 1 },
  aiBtn: {
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  saveStickyBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#10B981',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  assetsLink: {
    fontSize: 13,
    color: '#4F46E5',
    textDecoration: 'none',
    padding: '8px 12px',
    border: '1px solid #C7D2FE',
    borderRadius: 6,
  },
  openBuilder: {
    fontSize: 13,
    color: '#fff',
    backgroundColor: '#4F46E5',
    padding: '8px 14px',
    borderRadius: 6,
    textDecoration: 'none',
    fontWeight: 600,
  },
  inlineLink: { color: '#4F46E5', textDecoration: 'underline' },

  body: {
    display: 'flex',
    gap: 24,
    maxWidth: 1100,
    margin: '24px auto',
    padding: '0 24px',
    alignItems: 'flex-start',
  },
  toc: {
    width: 220,
    flexShrink: 0,
    position: 'sticky',
    top: 80,
    paddingTop: 8,
  },
  tocList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  tocLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    fontSize: 13,
    color: '#475569',
    textDecoration: 'none',
    borderRadius: 6,
  },
  tocDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#94A3B8',
    flexShrink: 0,
  },
  tocError: { fontSize: 11, color: '#c00', marginTop: 12 },

  main: { flex: 1, minWidth: 0 },
  group: {
    marginBottom: 28,
    scrollMarginTop: 80, // leaves space below the sticky header on anchor jump
  },
  groupHeading: {
    margin: '0 0 12px',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 12,
    scrollMarginTop: 80,
  },
  cardTitle: { margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0F172A' },
  cardHint: { margin: '0 0 16px', fontSize: 12, color: '#94A3B8', lineHeight: 1.5 },

  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: '#475569' },
  fieldHint: { fontSize: 11, color: '#94A3B8' },
  input: {
    flex: 1,
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid #CBD5E1',
    borderRadius: 4,
  },

  assetRow: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  assetControls: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 },
  pickBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#fff',
    border: '1px solid #C7D2FE',
    borderRadius: 6,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  clearBtn: {
    padding: '4px 10px',
    fontSize: 11,
    color: '#64748B',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    textDecoration: 'underline',
  },
  assetUrl: {
    fontSize: 11,
    color: '#94A3B8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  preview: {
    objectFit: 'cover' as const,
    borderRadius: 6,
    border: '1px solid #CBD5E1',
    backgroundColor: '#F8FAFC',
    flexShrink: 0,
  },
  previewEmpty: {
    borderRadius: 6,
    border: '1px dashed #CBD5E1',
    backgroundColor: '#F8FAFC',
    color: '#94A3B8',
    fontSize: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  empty: {
    padding: 40,
    textAlign: 'center' as const,
    color: '#94A3B8',
    fontFamily: 'system-ui, sans-serif',
  },
};
